import React, { useEffect, useMemo, useState } from "react";
import styles from "./WeatherPanel.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faCloudSun, faCloud, faCloudRain, faCloudBolt, faSnowflake, faMoon, faCloudMoon } from "@fortawesome/free-solid-svg-icons";

const defaultCoords = { lat: -23.5505, lon: -46.6333 }; // São Paulo fallback

function useWeather() {
  const [coords, setCoords] = useState(defaultCoords);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [place, setPlace] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const getLocation = () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(defaultCoords);
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
          () => resolve(defaultCoords),
          { maximumAge: 60_000, timeout: 4_000 }
        );
      });

    const fetchWeather = async () => {
      try {
        const loc = await getLocation();
        if (cancelled) return;
        setCoords(loc);

        const apiBase = (process.env.REACT_APP_API_URL?.replace(/\/$/, "") || window.location.origin);
        const url = `${apiBase}/weather/forecast?lat=${loc.lat}&lon=${loc.lon}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Falha ao obter clima");
        const json = await res.json();
        if (cancelled) return;
        setData(json);

        try {
          const gres = await fetch(`${apiBase}/weather/reverse-geocode?lat=${loc.lat}&lon=${loc.lon}&lang=pt`);
          if (gres.ok) {
            const gj = await gres.json();
            const first = Array.isArray(gj?.results) && gj.results.length ? gj.results[0] : null;
            if (!cancelled && first) {
              setPlace({
                name: first.name || first.locality || first.admin2 || first.admin1 || first.country || null,
                admin1: first.admin1 || first.admin2 || null,
                country: first.country || first.country_code || null,
              });
            }
          }
        } catch (_) {}
      } catch (e) {
        if (!cancelled) setError(e.message || "Erro ao carregar clima");
      }
    };

    fetchWeather();
    return () => { cancelled = true; };
  }, []);

  return { coords, data, error, place };
}

function pickTheme(weatherCode, tempC) {
  const code = Number(weatherCode);
  const scorching = tempC >= 34;
  const hot = tempC >= 28;
  const freezing = tempC <= 10;
  const cold = tempC <= 16;

  if ([0, 1].includes(code)) return freezing ? "freezing" : cold ? "cold" : scorching ? "scorching" : hot ? "hot" : "sunny";
  if ([2, 3, 45, 48].includes(code)) return (freezing || cold) ? "cold" : "cloudy";
  if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(code)) return freezing ? "cold" : "rainy";
  if ([71, 73, 75, 85, 86].includes(code)) return "freezing";
  if ([95, 96, 99].includes(code)) return "storm";
  return freezing ? "freezing" : cold ? "cold" : scorching ? "scorching" : hot ? "hot" : "cloudy";
}

function iconForTheme(theme, code, isDay) {
  // Open-Meteo manda is_day (0/1) no current_weather — sem isso, código 0/1
  // (céu limpo) sempre virava sol, mesmo às 21h. Só troca o ícone por lua
  // nos códigos de céu limpo/parcialmente limpo à noite; os outros (nuvem,
  // chuva, tempestade) já não dependem de horário pra fazer sentido.
  if (isDay === false && [0, 1].includes(Number(code))) {
    return Number(code) === 1 ? faCloudMoon : faMoon;
  }
  const map = {
    sunny: faSun, hot: faCloudSun, scorching: faSun,
    cloudy: faCloud, rainy: faCloudRain,
    storm: faCloudBolt, cold: faSnowflake, freezing: faSnowflake,
  };
  return map[theme] || faCloud;
}

const PHRASES = {
  scorching: [
    "Calorão intenso - hidrate antes, durante e depois.",
    "Temperatura extrema: treino leve ou coberto é sabedoria.",
    "Calor acima de 34° pede respeito - adapte e avance.",
    "Mais quente que o esperado. Mais forte do que parece.",
    "Extremo lá fora, controlado aqui dentro.",
    "O sol não perdoa - sua disciplina, sim.",
    "Temperatura máxima exige cuidado máximo. Vai.",
    "Nesse calor, cada rep vale por dois.",
  ],
  freezing: [
    "Congelando lá fora - o movimento aquece tudo.",
    "Abaixo de 10°: treinar aqui faz ainda mais sentido.",
    "Frio extremo, determinação absoluta.",
    "Neve ou chuva de inverno - você não para.",
    "O termômetro caiu; sua motivação não.",
    "Dia de frio intenso é dia de treino intenso.",
    "Quem treina no congelante, treina de verdade.",
    "Temperatura lá embaixo, resultados lá em cima.",
  ],
  sunny: [
    "Dia aberto, sem desculpas - bora treinar.",
    "Sol de fora, chama por dentro. Vai.",
    "Clima ideal pra superar o de ontem.",
    "Aqui ou na academia, hoje você se move.",
    "A luz do dia pede que você apareça.",
    "Sol e disposição: ingredientes do dia.",
    "Aproveite a clareza - mente e corpo em ação.",
    "Tempo bom demais pra ficar parado.",
  ],
  hot: [
    "Calor não para os fortes - hidrate e vá.",
    "Adapte o ritmo, mas não pare.",
    "Treine cedo, aproveite o dia. Beba água.",
    "O calor aumenta a dificuldade - e o mérito.",
    "30° lá fora? Seu treino vale em dobro.",
    "Sua meta não tem temperatura mínima.",
    "Corpo quente treina mais eficiente. Aproveite.",
    "No calor também se constroem resultados.",
  ],
  cloudy: [
    "Nuvens lá fora, nenhuma na sua meta.",
    "Sem sol, sem desculpa. Foco no plano.",
    "Clima neutro pede determinação pura.",
    "Nublado é perfeito pra quem não precisa de sol pra se mover.",
    "A consistência não espera o tempo abrir.",
    "Dia cinza, resultado real.",
    "Nada de lá fora muda o que você consegue aqui dentro.",
    "Hoje é dia de disciplina silenciosa.",
  ],
  rainy: [
    "Chuva lá fora, consistência aqui dentro.",
    "A chuva fica com preguiça - você, não.",
    "Dias de chuva separam quem quer de quem precisa.",
    "Quem treina na chuva nunca para.",
    "O tempo fecha - sua rotina não.",
    "Molhado ou seco, o resultado é o mesmo: real.",
    "Chuvoso é dia de treino sério.",
    "Enquanto a chuva cai, você sobe de nível.",
  ],
  storm: [
    "Tempestade lá fora - calma e foco aqui dentro.",
    "Caos do tempo, controle do movimento.",
    "Transforme a energia da tempestade em treino.",
    "Nos dias difíceis se constroem os resultados maiores.",
    "O trovão avisa: hoje você vai além.",
    "Força de fora, força de dentro.",
    "Dias de tempestade são dias de superação.",
    "Quando tudo balança lá fora, sua disciplina se firma.",
  ],
  cold: [
    "Frio é só o começo - o movimento aquece tudo.",
    "Sair do calor da cama é o treino mais difícil.",
    "Gelou? Seu corpo pede movimento.",
    "O frio passa. O resultado fica.",
    "Friozinho bom pra queimar mais em menos tempo.",
    "Quem treina no frio treina de verdade.",
    "Agasalhe a preguiça, liberte o esforço.",
    "Os melhores dias pra treinar são exatamente esses.",
  ],
};

function getPeriod() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 18) return 'afternoon';
  return 'evening';
}

// Frases standalone por período - alternadas com frases de tema, sem concatenação
const PERIOD_PHRASES = {
  morning: [
    "Bom dia - o melhor treino começa cedo.",
    "Manhã de treino é manhã ganho.",
    "Acorde o corpo antes de acordar o dia.",
    "Quem treina de manhã não cancela à tarde.",
    "O dia já começou bem - aproveite o embalo.",
    "Manhã fresca, mente clara, corpo em ação.",
  ],
  afternoon: [
    "A tarde pede movimento - não deixe ela passar.",
    "Intervalo produtivo: 45 minutos de treino.",
    "Tarde de treino fecha o dia com energia.",
    "Meio do dia, meio do esforço - vá em frente.",
    "Não espere a tarde acabar pra se mexer.",
    "Hora de render mais do que o expediente.",
  ],
  evening: [
    "Feche o dia com o treino feito.",
    "Noite de treino é noite bem vivida.",
    "O cansaço do treino é o melhor sonífero.",
    "Último round do dia - faça valer.",
    "Enquanto a cidade descansa, você avança.",
    "Noite boa começa com suor honesto.",
  ],
};

function getPhrase(theme) {
  const list = PHRASES[theme] || PHRASES.cloudy;
  const seed = Math.floor(Date.now() / 60_000);

  // A cada 4 ciclos, usa frase de período (standalone, sem concatenar com tema)
  if (seed % 4 === 0) {
    const period = getPeriod();
    const periodList = PERIOD_PHRASES[period];
    return periodList[seed % periodList.length];
  }
  return list[seed % list.length];
}

export default function WeatherPanel({ isMobile = false, compact = false }) {
  const { data, error, place } = useWeather();

  const weather = useMemo(() => {
    if (!data?.current && !data?.current_weather) return null;
    const temp = Math.round(
      Number(
        (data.current && data.current.temperature_2m) ??
        (data.current_weather && data.current_weather.temperature) ??
        0
      )
    );
    const code = Number(
      (data.current && data.current.weather_code) ??
      (data.current_weather && data.current_weather.weathercode) ??
      0
    );
    const tmax = Math.round(Number(data.daily?.temperature_2m_max?.[0] ?? temp));
    const tmin = Math.round(Number(data.daily?.temperature_2m_min?.[0] ?? temp));
    const theme = pickTheme(code, temp);
    const phrase = getPhrase(theme);
    // is_day vem no current_weather do Open-Meteo (0 ou 1) — default true pra
    // não quebrar caso a API omita o campo por algum motivo.
    const rawIsDay = data.current?.is_day ?? data.current_weather?.is_day;
    const isDay = rawIsDay === undefined ? true : Number(rawIsDay) === 1;
    return { temp, tmax, tmin, theme, phrase, code, isDay };
  }, [data]);

  const city = useMemo(() =>
    place?.name ||
    place?.locality ||
    place?.admin1 ||
    place?.country ||
    null
  , [place]);

  const info = weather ? { ...weather, city } : null;

  if (error) {
    if (compact) return null;
    return (
      <div className={`${styles.box} ${styles.fallback}`}>
        <div className={styles.sub}>Siga com o plano - 1% melhor hoje.</div>
      </div>
    );
  }

  if (!info) {
    if (compact) return null;
    return (
      <div className={`${styles.box} ${styles.skeleton}`}>
        <div className={styles.mainRow}><span className={styles.loadBar} /></div>
        <div className={styles.sub}><span className={styles.loadBarSmall} /></div>
      </div>
    );
  }

  const themeClass = styles[`t_${info.theme}`] || styles.t_cloudy;
  const weatherIcon = iconForTheme(info.theme, info.code, info.isDay);

  if (compact) {
    return (
      <span className={`${styles.chip} ${themeClass}`}>
        <FontAwesomeIcon icon={weatherIcon} />
        {info.city && <span>{info.city}</span>}
        <strong>{info.temp}°C</strong>
        <span className={styles.chipRange}>{info.tmin}° / {info.tmax}°</span>
      </span>
    );
  }

  if (isMobile) {
    return (
      <div className={`${styles.box} ${themeClass} ${styles.mobile}`}>
        <div className={styles.mainRow}>
          <span className={styles.emoji}><FontAwesomeIcon icon={weatherIcon} /></span>
          <div className={styles.tempBlock}>
            <strong className={styles.temp}>{info.temp}°C</strong>
            <span className={styles.range}>{info.tmin}–{info.tmax}°</span>
          </div>
          <span className={styles.city}>{info.city || "Local"}</span>
        </div>
        <div className={styles.sub}>{info.phrase}</div>
      </div>
    );
  }

  return (
    <div className={`${styles.box} ${themeClass}`}>
      <div className={styles.mainRow}>
        <span className={styles.emoji}><FontAwesomeIcon icon={weatherIcon} /></span>
        <div className={styles.titleBlock}>
          <div className={styles.cityDesktop}>{info.city || "Previsão local"}</div>
          <div className={styles.sub}>{info.phrase}</div>
        </div>
        <div className={styles.spacer} />
        <div className={styles.temps}>
          <strong className={styles.temp}>{info.temp}°C</strong>
          <span className={styles.range}>{info.tmin}° / {info.tmax}°</span>
        </div>
      </div>
    </div>
  );
}
